import { Suspense, lazy, useEffect, useState, type ReactNode } from 'react';
import {
  BrowserRouter,
  Link,
  Navigate,
  Route,
  Routes,
  useLocation,
  useParams,
} from 'react-router-dom';
import { RESERVED_SLUGS, validateSlug } from '@ugc/shared';
import { AuthProvider, useAuth } from './auth/AuthContext';
import { loadConfig } from './config';
import { ForgotPasswordPage } from './pages/ForgotPasswordPage';
import { LandingPage } from './pages/LandingPage';
import { LoginPage } from './pages/LoginPage';
import { ResetPasswordPage } from './pages/ResetPasswordPage';
import { SignupPage } from './pages/SignupPage';
import { VerifyPage } from './pages/VerifyPage';

/**
 * Split points, not just conveniences: most traffic is visitors landing on a
 * profile, and they must never download the editor (or the reverse).
 */
const EditorPage = lazy(() =>
  import('./pages/EditorPage').then((m) => ({ default: m.EditorPage })),
);
const PublicProfilePage = lazy(() =>
  import('./pages/PublicProfilePage').then((m) => ({ default: m.PublicProfilePage })),
);

type BootState =
  | { phase: 'loading' }
  | { phase: 'ready' }
  | { phase: 'failed'; message: string };

export function App() {
  const [boot, setBoot] = useState<BootState>({ phase: 'loading' });

  useEffect(() => {
    let active = true;
    loadConfig().then(
      () => {
        if (active) setBoot({ phase: 'ready' });
      },
      (error: unknown) => {
        if (active) {
          setBoot({
            phase: 'failed',
            message: error instanceof Error ? error.message : 'Unknown error.',
          });
        }
      },
    );
    return () => {
      active = false;
    };
  }, []);

  // Nothing renders before config resolves: the auth provider needs the user
  // pool ids and every API call needs a base URL.
  if (boot.phase === 'loading') return <FullPageLoader />;
  if (boot.phase === 'failed') return <BootFailure message={boot.message} />;

  return (
    <BrowserRouter>
      {/* Router outside the provider so auth code is free to navigate. */}
      <AuthProvider>
        <SessionGate>
          <Suspense fallback={<FullPageLoader />}>
            <Routes>
              <Route path="/" element={<LandingPage />} />
              <Route path="/login" element={<LoginPage />} />
              <Route path="/signup" element={<SignupPage />} />
              <Route path="/verify" element={<VerifyPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route
                path="/app"
                element={
                  <RequireAuth>
                    <EditorPage />
                  </RequireAuth>
                }
              />
              {/* Multi-segment URLs only; ranking, not order, keeps this away
                  from the single-segment slug route below. */}
              <Route path="*" element={<NotFound />} />
              {/* Last, always: the slug route owns whatever is left of the
                  namespace and must never be read before a real app route. */}
              <Route path="/:slug" element={<SlugRoute />} />
            </Routes>
          </Suspense>
        </SessionGate>
      </AuthProvider>
    </BrowserRouter>
  );
}

/** Holds the whole tree until the stored Cognito session has been checked. */
function SessionGate({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  if (status === 'loading') return <FullPageLoader />;
  return <>{children}</>;
}

/**
 * Sends signed-out visitors to /login carrying where they were headed, so the
 * link they followed still works once they are in.
 */
function RequireAuth({ children }: { children: ReactNode }) {
  const { status } = useAuth();
  const location = useLocation();

  if (status === 'loading') return <FullPageLoader />;
  if (status !== 'authenticated') {
    const from = `${location.pathname}${location.search}`;
    // Both: the query survives a reload of /login, the state survives a slug
    // that would look odd in the address bar.
    return (
      <Navigate to={`/login?next=${encodeURIComponent(from)}`} state={{ from }} replace />
    );
  }
  return <>{children}</>;
}

/**
 * The public profile route shares its namespace with every app route and with
 * paths the CDN owns, so a reserved word is a 404 here — it must never turn
 * into a profile lookup that could shadow /login or /api.
 */
function SlugRoute() {
  const { slug } = useParams();
  const candidate = (slug ?? '').toLowerCase();

  if (RESERVED_SLUGS.has(candidate) || validateSlug(candidate) !== null) {
    return <NotFound />;
  }
  // Slugs are canonically lowercase; redirect rather than 404 a shared link
  // that picked up capitals along the way.
  if (slug !== undefined && slug !== candidate) {
    return <Navigate to={`/${candidate}`} replace />;
  }
  return <PublicProfilePage />;
}

function FullPageLoader() {
  return (
    <div className="loader-page" role="status" aria-live="polite">
      <span className="spinner" aria-hidden="true" />
      <span className="visually-hidden">Loading</span>
    </div>
  );
}

function BootFailure({ message }: { message: string }) {
  return (
    <main className="center-page">
      <div className="card card--narrow">
        <div className="card__body stack">
          <h1 className="card__title">Something went wrong</h1>
          <p className="muted">This page could not load its settings.</p>
          <p className="error-text">{message}</p>
          <button type="button" className="btn btn--primary" onClick={() => location.reload()}>
            Try again
          </button>
        </div>
      </div>
    </main>
  );
}

function NotFound() {
  return (
    <main className="center-page">
      <div className="card card--narrow">
        <div className="card__body stack">
          <p className="eyebrow">404</p>
          <h1 className="card__title">Page not found</h1>
          <p className="muted">That link does not point at anything here.</p>
          <Link className="btn btn--secondary" to="/">
            Go home
          </Link>
        </div>
      </div>
    </main>
  );
}
